/**
 * The mockup's icon set, lifted verbatim.
 *
 * Inline SVG rather than an icon package: there are a dozen of them, they are
 * already drawn, and `stroke: currentColor` means they follow the palette
 * through a light-mode swap without a second thought.
 */
const PATHS = {
  overview: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  cycles: (
    <>
      <polyline points="21 3 21 8 16 8" />
      <polyline points="3 21 3 16 8 16" />
      <path d="M3.9 9.5A8 8 0 0 1 21 8" />
      <path d="M20.1 14.5A8 8 0 0 1 3 16" />
    </>
  ),
  ledger: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <line x1="8" y1="8.5" x2="16" y2="8.5" />
      <line x1="8" y1="12.5" x2="16" y2="12.5" />
      <line x1="8" y1="16.5" x2="13" y2="16.5" />
    </>
  ),
  clients: (
    <>
      <path d="M16 20v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 18.4V20" />
      <circle cx="9.5" cy="8" r="3.4" />
      <path d="M21 20v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.6 4.8a3.4 3.4 0 0 1 0 6.4" />
    </>
  ),
  approvals: (
    <>
      <path d="M21 11.1V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10.5" />
      <polyline points="8.5 11.5 11.5 14.5 21 4.5" />
    </>
  ),
  internal: (
    <path d="M14.6 6.2a1 1 0 0 0 0 1.4l1.8 1.8a1 1 0 0 0 1.4 0l3.5-3.5a6 6 0 0 1-7.9 7.8l-6.6 6.6a2.1 2.1 0 0 1-3-3l6.6-6.6a6 6 0 0 1 7.8-7.9z" />
  ),
  disputes: (
    <>
      <path d="M10.3 4 2.4 17.6A1.9 1.9 0 0 0 4 20.5h16a1.9 1.9 0 0 0 1.6-2.9L13.7 4a1.9 1.9 0 0 0-3.4 0z" />
      <line x1="12" y1="9.5" x2="12" y2="13.5" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <line x1="12" y1="1.8" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22.2" />
      <line x1="4.4" y1="4.4" x2="6" y2="6" />
      <line x1="18" y1="18" x2="19.6" y2="19.6" />
      <line x1="1.8" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22.2" y2="12" />
      <line x1="4.4" y1="19.6" x2="6" y2="18" />
      <line x1="18" y1="6" x2="19.6" y2="4.4" />
    </>
  ),
  logout: (
    <>
      <path d="M9.5 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4.5" />
      <polyline points="16 16.5 20.5 12 16 7.5" />
      <line x1="20.5" y1="12" x2="9.5" y2="12" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8.5a6 6 0 1 0-12 0c0 6-3 7.5-3 7.5h18s-3-1.5-3-7.5" />
      <path d="M13.7 20a2 2 0 0 1-3.4 0" />
    </>
  ),
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  chat: <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.9L3 20.5l1.5-4.5A8.4 8.4 0 0 1 3.6 12a8.4 8.4 0 0 1 8.4-8.4h.5A8.4 8.4 0 0 1 21 11.5z" />,
  check: <polyline points="4 12.5 9.5 18 20 6" />,
  phone: (
    <path d="M21.5 16.9v2.8a1.9 1.9 0 0 1-2.1 1.9 18.7 18.7 0 0 1-8.2-2.9 18.4 18.4 0 0 1-5.7-5.7A18.7 18.7 0 0 1 2.6 4.7 1.9 1.9 0 0 1 4.5 2.6h2.8a1.9 1.9 0 0 1 1.9 1.6c.1 1 .4 1.9.7 2.8a1.9 1.9 0 0 1-.4 2L8.3 10.2a15 15 0 0 0 5.5 5.5l1.2-1.2a1.9 1.9 0 0 1 2-.4c.9.3 1.8.6 2.8.7a1.9 1.9 0 0 1 1.7 2z" />
  ),
  mail: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <polyline points="3 6 12 13 21 6" />
    </>
  ),
  ellipsis: (
    <>
      <circle cx="5" cy="12" r="1.1" />
      <circle cx="12" cy="12" r="1.1" />
      <circle cx="19" cy="12" r="1.1" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4.8" width="18" height="16.2" rx="2" />
      <line x1="3" y1="9.6" x2="21" y2="9.6" />
      <line x1="8.2" y1="2.6" x2="8.2" y2="6" />
      <line x1="15.8" y1="2.6" x2="15.8" y2="6" />
    </>
  ),
  eye: (
    <>
      <path d="M1.8 12S5.5 5.2 12 5.2 22.2 12 22.2 12 18.5 18.8 12 18.8 1.8 12 1.8 12z" />
      <circle cx="12" cy="12" r="3.1" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="9.5" />
      <line x1="12" y1="7.5" x2="12" y2="13" />
      <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
    </>
  ),
  // Sits slightly above centre so the handle has room inside the 24 box without
  // the lens shrinking — at 13px a smaller circle stops reading as a lens.
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <line x1="15.2" y1="15.2" x2="20.5" y2="20.5" />
    </>
  ),
  // Inset from the 24 box so it reads at the same weight as `check` beside it,
  // rather than as a larger glyph.
  x: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  ),
  // One person, for the signed-in row. `clients` is two people and already
  // means something else in the nav — reusing it there would read as a link to
  // the client list rather than as who you are.
  user: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20v-1.4A4.6 4.6 0 0 1 9.6 14h4.8a4.6 4.6 0 0 1 4.6 4.6V20" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="1.5" />
      <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </>
  ),
  // Balance scale — the same motif as the favicon, for the Ledger Reconciler
  // nav entry: two sides, weighed against each other.
  scale: (
    <>
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="6" y1="7" x2="18" y2="7" />
      <path d="M6 7 3 13a3.2 3.2 0 0 0 6 0z" />
      <path d="M18 7l-3 6a3.2 3.2 0 0 0 6 0z" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </>
  ),
};

export default function Icon({ name, size = 16 }) {
  const path = PATHS[name];
  if (!path) return null;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      {path}
    </svg>
  );
}
